import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { AuthContext, API } from '@/App';
import axios from 'axios';
import { Users, Briefcase, Calendar, Sparkles, LayoutDashboard, LogOut, MessageSquare } from 'lucide-react';

const Navigation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, token, logout } = React.useContext(AuthContext);
  const [emailFailures24h, setEmailFailures24h] = useState(0);

  // Admins only: poll the email delivery stats so the Admin nav button can show
  // a red dot whenever transactional emails are failing, visible from any page.
  useEffect(() => {
    if (user?.role !== 'admin' || !token) return;
    let cancelled = false;
    const fetchStats = async () => {
      try {
        const res = await axios.get(`${API}/admin/email/stats`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setEmailFailures24h(res.data?.last_24h?.failed ?? 0);
      } catch (e) {
        // Silent, nav must never crash on stats fetch failure
      }
    };
    fetchStats();
    const interval = setInterval(fetchStats, 60000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [user, token]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/members', label: 'Members', icon: Users },
    { path: '/businesses', label: 'Businesses', icon: Briefcase },
    { path: '/events', label: 'Events', icon: Calendar },
    { path: '/opportunities', label: 'Opportunities', icon: Sparkles },
    { path: '/forum', label: 'Forum', icon: MessageSquare },
  ];
  
  const getMembershipBadge = () => {
    if (user?.membership_tier === 'premium') return <span className="ml-1 text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full">Premium</span>;
    if (user?.membership_tier === 'corporate') return <span className="ml-1 text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">Corporate</span>;
    return null;
  };

  return (
    <nav className="glass border-b" data-testid="navigation">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          <Link to={user ? "/dashboard" : "/"} className="flex items-center gap-3" data-testid="nav-logo">
            <img src="/logo.png" alt="Her Enterprise Hub" className="h-10 w-auto object-contain" style={{ maxWidth: '200px' }} />
          </Link>

          <div className="hidden md:flex items-center gap-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link key={item.path} to={item.path}>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    className={`flex items-center gap-2 ${isActive ? 'bg-rose-100 text-rose-700' : ''}`}
                    data-testid={`nav-${item.label.toLowerCase()}`}
                  >
                    <Icon className="w-4 h-4" />
                    {item.label}
                  </Button>
                </Link>
              );
            })}
          </div>

          <div className="flex items-center gap-2">
            {!user ? (
              // Anonymous visitors: show Sign In / Join CTAs
              <>
                <Link to="/membership">
                  <Button variant="ghost" size="sm" data-testid="nav-membership">
                    Membership
                  </Button>
                </Link>
                <Link to="/">
                  <Button variant="outline" size="sm" data-testid="nav-signin" className="text-rose-700 border-rose-300 font-semibold">
                    Sign In
                  </Button>
                </Link>
                <Link to="/">
                  <Button size="sm" data-testid="nav-signup" className="bg-rose-700 hover:bg-rose-800 text-white">
                    Join Us
                  </Button>
                </Link>
              </>
            ) : (
              <>
                <Link to="/membership">
                  <Button variant="ghost" size="sm" data-testid="nav-membership">
                    Membership
                    {getMembershipBadge()}
                  </Button>
                </Link>
                {(user?.role === 'partner' || user?.role === 'admin') && (
                  <Link to="/partner-portal">
                    <Button variant="ghost" size="sm" data-testid="nav-partner-portal" className="text-purple-600 font-semibold">
                      Partner
                    </Button>
                  </Link>
                )}
                {user?.role === 'admin' && (
                  <Link to="/admin">
                    <Button variant="ghost" size="sm" data-testid="nav-admin" className="text-rose-700 font-semibold relative">
                      Admin
                      {emailFailures24h > 0 && (
                        <span
                          className="absolute -top-0.5 -right-0.5 flex h-2.5 w-2.5"
                          data-testid="nav-admin-email-failure-dot"
                          title={`${emailFailures24h} email failure(s) in the last 24h`}
                        >
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-600"></span>
                        </span>
                      )}
                    </Button>
                  </Link>
                )}
                {/* Show different profile link for employees */}
                {user?.employee_id ? (
                  <Link to="/employee/profile">
                    <Button variant="ghost" size="sm" className="text-rose-700 font-semibold">
                      My Profile
                    </Button>
                  </Link>
                ) : (
                  <Link to={`/profile/${user?.id}`}>
                    <Button variant="ghost" size="sm" className="text-rose-700 font-semibold" data-testid="nav-profile">
                      Profile
                    </Button>
                  </Link>
                )}
                <Button onClick={handleLogout} variant="outline" size="sm" className="flex items-center gap-1" data-testid="nav-logout">
                  <LogOut className="w-4 h-4" />
                  Logout
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;
